'use client';

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';

const CHECKSHOP_SHOP_ID = 'cmpk1hwes000z7cmr241ig998';
const CHECKSHOP_DOMAIN = 'printen.eu';

type CheckShopBadgeData = {
  score?: number;
  avgRating?: number;
  reviewCount?: number;
  domain?: string;
};

type CheckShopStarsRowProps = {
  className?: string;
  fallbackReviewCount?: number;
};

function StarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#fdc700"
      stroke="#fdc700"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="block"
    >
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </svg>
  );
}

function ratingLabel(data: CheckShopBadgeData | null, reviewCount: number, reviewLabel: string) {
  if (typeof data?.avgRating === 'number' && data.avgRating > 0) {
    return `(${(data.avgRating * 2).toFixed(1)}/10 - ${reviewCount} ${reviewLabel})`;
  }
  if (typeof data?.score === 'number') {
    return `(${data.score}/100 - ${reviewCount} ${reviewLabel})`;
  }
  return `(${reviewCount} ${reviewLabel})`;
}

export function CheckShopStarsRow({ className = '', fallbackReviewCount = 127 }: CheckShopStarsRowProps) {
  const locale = useLocale();
  const [data, setData] = useState<CheckShopBadgeData | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(`https://checkshop.eu/api/v1/badge/${CHECKSHOP_SHOP_ID}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((badgeData) => {
        if (!cancelled && badgeData && !badgeData.error) {
          setData(badgeData);
        }
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const reviewCount = data?.reviewCount || fallbackReviewCount;
  const reviewLabel = locale === 'de' ? 'Bewertungen' : locale === 'fr' ? 'avis' : 'reviews';
  const ariaCopy = locale === 'de' ? 'CheckShop Bewertung' : locale === 'fr' ? 'Avis CheckShop' : locale === 'en' ? 'CheckShop rating' : 'CheckShop beoordeling';
  const label = ratingLabel(data, reviewCount, reviewLabel);
  const domain = data?.domain || CHECKSHOP_DOMAIN;

  return (
    <a
      href={`https://checkshop.eu/shop/${domain}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 text-sm text-gray-600 no-underline transition hover:text-gray-900 ${className}`}
      aria-label={`${ariaCopy} ${label}`}
    >
      <span className="inline-flex items-center" aria-hidden="true">
        <StarIcon />
        <StarIcon />
        <StarIcon />
        <StarIcon />
        <StarIcon />
      </span>
      <span>{label}</span>
    </a>
  );
}
